import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AdminShell, RequireAdmin } from './AdminShell';
import { blogApi, slugify } from '../../lib/adminApi';
import { apiAuthed } from '../../lib/adminApi';
import RichEditor from '../../components/admin/RichEditor';
import { ArrowLeft, ImagePlus, Loader2, Sparkles, Wand2 } from 'lucide-react';

const CATEGORIES = ['Strategy', 'Product', 'Case studies', 'Insights'];

const emptyPost = () => ({
  slug: '',
  title: '',
  excerpt: '',
  body_html: '<p>Write your first paragraph here…</p>',
  cover_url: '',
  category: 'Strategy',
  author_name: 'The 5DBS Team',
  author_title: 'Publisher Insights',
  status: 'draft',
  publish_at: null,
});

const AdminBlogEditor = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const isEdit = Boolean(slug);
  const [post, setPost] = useState(emptyPost());
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const [touched, setTouched] = useState(false);
  const coverInputRef = useRef(null);

  useEffect(() => {
    if (!isEdit) return;
    blogApi.get(slug).then((p) => setPost({ ...emptyPost(), ...p })).catch((e) => setErr(e?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [slug, isEdit]);

  const set = (patch) => { setPost((p) => ({ ...p, ...patch })); setTouched(true); };

  const onTitle = (v) => {
    // Only auto-slugify while creating a new post AND before the user has manually touched slug.
    if (!isEdit && (!post.slug || post.slug === slugify(post.title))) {
      set({ title: v, slug: slugify(v) });
    } else set({ title: v });
  };

  const uploadCover = async (file) => {
    setUploading(true); setErr('');
    try {
      const { url } = await blogApi.upload(file);
      set({ cover_url: url });
    } catch (e) {
      setErr('Cover upload failed.');
    } finally { setUploading(false); }
  };

  const [aiBusy, setAiBusy] = useState(false);
  const generateWithAI = async () => {
    const topic = window.prompt('Topic for the AI to write about:', post.title || '');
    if (!topic) return;
    setAiBusy(true); setErr('');
    try {
      const { data } = await apiAuthed.post('/ai/article', { topic });
      set({
        title: post.title || data.title || topic,
        slug: post.slug || slugify(data.title || topic),
        excerpt: post.excerpt || data.excerpt || '',
        body_html: data.html || post.body_html,
      });
    } catch (e) {
      setErr('AI article failed: ' + (e?.response?.data?.detail || e.message));
    } finally { setAiBusy(false); }
  };

  const generateCoverWithAI = async () => {
    const prompt = window.prompt('Describe the cover image:', post.title || '');
    if (!prompt) return;
    setUploading(true); setErr('');
    try {
      const { data } = await apiAuthed.post('/ai/cover', { prompt });
      set({ cover_url: data.url });
    } catch (e) {
      setErr('AI cover failed: ' + (e?.response?.data?.detail || e.message));
    } finally { setUploading(false); }
  };

  const save = async (nextStatus) => {
    setErr(''); setSaving(true);
    try {
      const payload = { ...post, status: nextStatus || post.status };
      if (!payload.slug) payload.slug = slugify(payload.title) || `post-${Date.now()}`;
      const saved = isEdit
        ? await blogApi.update(slug, payload)
        : await blogApi.create(payload);
      setTouched(false);
      navigate(isEdit ? '/admin/blog' : `/admin/blog/${saved.slug}/edit`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === 'string' ? d : (Array.isArray(d) ? d.map(x => x.msg).join(' ') : 'Save failed.'));
    } finally { setSaving(false); }
  };

  return (
    <RequireAdmin>
      <AdminShell
        title={isEdit ? 'Edit post' : 'New post'}
        actions={
          <>
            <Link to="/admin/blog" className="inline-flex items-center gap-1.5 text-body-muted hover:text-navy text-[12px] font-bold uppercase tracking-[0.08em]"><ArrowLeft className="h-4 w-4" /> Back</Link>
            <button type="button" disabled={saving} onClick={() => save('draft')} data-testid="editor-save-draft"
              className="inline-flex items-center gap-2 h-10 px-5 rounded-md border border-slate-300 text-navy text-[12px] font-bold uppercase tracking-[0.08em] hover:border-navy transition-colors disabled:opacity-60">
              Save draft
            </button>
            <button type="button" disabled={saving} onClick={() => save('published')} data-testid="editor-publish"
              className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-brand-pink hover:bg-brand-pink/90 text-white text-[12px] font-bold uppercase tracking-[0.08em] shadow-mk hover:shadow-mk-lg transition-all duration-300 disabled:opacity-60">
              {saving ? 'Saving…' : (post.status === 'published' ? 'Update published' : 'Publish')}
            </button>
          </>
        }
      >
        {loading ? (
          <div className="flex items-center gap-2 text-body-muted"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8 space-y-5">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-[0.14em] text-body-muted">Title</label>
                <input data-testid="editor-title" value={post.title} onChange={(e) => onTitle(e.target.value)}
                  className="mt-1 w-full h-12 bg-transparent border-b-2 border-slate-200 focus:border-brand-blue text-navy text-[24px] font-black tracking-tight outline-none placeholder:text-slate-300"
                  placeholder="Your headline"
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-[0.14em] text-body-muted">Excerpt</label>
                <textarea rows={2} data-testid="editor-excerpt" value={post.excerpt || ''} onChange={(e) => set({ excerpt: e.target.value })}
                  className="mt-1 w-full bg-transparent border-b-2 border-slate-200 focus:border-brand-blue text-navy text-[15px] leading-[1.6] outline-none py-1 resize-none"
                  placeholder="One or two sentences shown in the blog list."
                />
              </div>
              <RichEditor value={post.body_html} onChange={(html) => set({ body_html: html })} />
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" onClick={generateWithAI} disabled={aiBusy} data-testid="editor-ai-article"
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-gradient-to-r from-brand-pink to-brand-blue text-white text-[12px] font-bold uppercase tracking-[0.08em] shadow-mk hover:shadow-mk-lg transition-all duration-300 disabled:opacity-60">
                  {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                  {aiBusy ? 'Writing…' : 'Write with AI'}
                </button>
                <span className="text-body-muted text-[12px]">Uses Replicate · Claude Sonnet 5</span>
              </div>
              {err && <p role="alert" data-testid="editor-error" className="text-rose-600 text-[13px]">{err}</p>}
              {touched && !saving && <p className="text-body-muted text-[12.5px]">Unsaved changes.</p>}
            </div>

            <aside className="lg:col-span-4 space-y-6">
              <div className="rounded-xl border border-slate-200 bg-white p-5">
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-body-muted">Slug</p>
                <input data-testid="editor-slug" value={post.slug} onChange={(e) => set({ slug: slugify(e.target.value) })}
                  className="mt-1 w-full h-10 bg-transparent border-b-2 border-slate-200 focus:border-brand-blue text-navy text-[14px] outline-none" />
                <p className="mt-2 text-body-muted text-[12px]">Lives at <code>/blog/{post.slug || 'your-slug'}</code></p>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-5">
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-body-muted mb-2">Cover image</p>
                {post.cover_url ? (
                  <div className="relative">
                    <img src={post.cover_url.startsWith('/files/') ? `${process.env.REACT_APP_BACKEND_URL}/api${post.cover_url}` : post.cover_url} alt="" className="w-full aspect-[16/9] object-cover rounded-lg" />
                    <button type="button" onClick={() => set({ cover_url: '' })}
                      className="absolute top-2 right-2 h-8 px-3 rounded-md bg-white/90 text-navy text-[11px] font-bold uppercase tracking-[0.08em] shadow-mk">
                      Replace
                    </button>
                  </div>
                ) : (
                  <button type="button" onClick={() => coverInputRef.current?.click()} data-testid="editor-cover-upload"
                    className="w-full aspect-[16/9] rounded-lg border-2 border-dashed border-slate-200 hover:border-brand-blue text-body-muted hover:text-brand-blue flex flex-col items-center justify-center gap-2 transition-colors">
                    {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-6 w-6" />}
                    <span className="text-[12px] font-bold uppercase tracking-[0.1em]">{uploading ? 'Uploading…' : 'Upload cover'}</span>
                  </button>
                )}
                <input ref={coverInputRef} type="file" accept="image/*" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadCover(f); e.target.value = ''; }} />
                <button type="button" onClick={generateCoverWithAI} disabled={uploading} data-testid="editor-ai-cover"
                  className="mt-3 inline-flex items-center gap-2 h-9 px-4 rounded-md border border-brand-pink/30 text-brand-pink text-[11.5px] font-bold uppercase tracking-[0.08em] hover:bg-brand-pink/10 transition-colors disabled:opacity-60">
                  <Sparkles className="h-4 w-4" /> Generate with AI
                </button>
                <input value={post.cover_url || ''} onChange={(e) => set({ cover_url: e.target.value })}
                  placeholder="Or paste an image URL"
                  className="mt-3 w-full h-9 bg-transparent border border-slate-200 rounded-md px-2 text-navy text-[13px] outline-none focus:border-brand-blue" />
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-body-muted">Category</p>
                  <select value={post.category} onChange={(e) => set({ category: e.target.value })}
                    className="mt-1 w-full h-10 bg-transparent border-b-2 border-slate-200 focus:border-brand-blue text-navy text-[14px] outline-none">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-body-muted">Author</p>
                  <input value={post.author_name} onChange={(e) => set({ author_name: e.target.value })}
                    className="mt-1 w-full h-10 bg-transparent border-b-2 border-slate-200 focus:border-brand-blue text-navy text-[14px] outline-none" />
                  <input value={post.author_title || ''} onChange={(e) => set({ author_title: e.target.value })}
                    placeholder="Author subtitle (optional)"
                    className="mt-2 w-full h-9 bg-transparent border-b-2 border-slate-200 focus:border-brand-blue text-navy text-[13px] outline-none" />
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-body-muted">Status</p>
                  <p className="mt-1 text-navy text-[14px] capitalize">{post.status}</p>
                </div>
              </div>
            </aside>
          </div>
        )}
      </AdminShell>
    </RequireAdmin>
  );
};

export default AdminBlogEditor;
